/* path: src/Utils/yeat.js */
import { DateTime } from 'luxon';

// Helper function to check if a reservation is a Yeat Last Minute reservation
export const isYeatLastMinute = (reservation) => {
  return (
    reservation &&
    reservation.reservationType === 'yeat' &&
    reservation.firstName === 'Yeat' &&
    reservation.lastName === 'Last Minute'
  );
};

// Internal helper: returns true if the given Yeat reservation time is in the past
// when the formattedDate is "today" in Europe/Brussels. Otherwise returns false.
const isPastYeatTimeForToday = (formattedDate, timeHHMM) => {
  if (!formattedDate || !timeHHMM) return false;

  const now = DateTime.now().setZone('Europe/Brussels');
  const date = DateTime.fromISO(formattedDate, { zone: 'Europe/Brussels' });
  if (!date.isValid) return false;

  const isToday = date.hasSame(now, 'day');
  if (!isToday) return false;

  // Parse time, allowing single-digit hour as well
  let dt = DateTime.fromFormat(timeHHMM, 'H:mm', { zone: 'Europe/Brussels' });
  if (!dt.isValid) {
    dt = DateTime.fromFormat(timeHHMM, 'HH:mm', { zone: 'Europe/Brussels' });
  }
  if (!dt.isValid) return false;

  dt = dt.set({ year: now.year, month: now.month, day: now.day });
  return dt < now;
};

// Helper function to check if a date has available Yeat reservations for the given guest count
export const checkYeatAvailability = (reservations, formattedDate, guests) => {
  if (!reservations || !Array.isArray(reservations) || !guests) {
    return false;
  }

  // Find all Yeat reservations for this date
  let yeatReservations = reservations.filter(
    (reservation) =>
      reservation.date === formattedDate && isYeatLastMinute(reservation)
  );

  if (yeatReservations.length === 0) {
    return false;
  }

  // Filter out past Yeat times if the formattedDate is today (Europe/Brussels)
  yeatReservations = yeatReservations.filter(
    (r) => !isPastYeatTimeForToday(formattedDate, r.time)
  );

  if (yeatReservations.length === 0) {
    return false;
  }

  // Check if any Yeat reservation can accommodate the guest count
  return yeatReservations.some((reservation) => {
    const minSeats = parseInt(reservation.minSeats, 10) || 0;
    const maxSeats = parseInt(reservation.maxSeats, 10) || 0;
    return guests >= minSeats && guests <= maxSeats;
  });
};

// Helper function to get Yeat times for a specific date
export const getYeatTimes = (reservations, formattedDate, guests) => {
  if (!reservations || !Array.isArray(reservations)) {
    return [];
  }

  // Find all Yeat reservations for this date that can accommodate the guest count
  let yeatReservations = reservations.filter((reservation) => {
    if (reservation.date !== formattedDate || !isYeatLastMinute(reservation)) {
      return false;
    }

    const minSeats = parseInt(reservation.minSeats, 10) || 0;
    const maxSeats = parseInt(reservation.maxSeats, 10) || 0;
    return guests >= minSeats && guests <= maxSeats;
  });

  // Filter out past Yeat times if the formattedDate is today (Europe/Brussels)
  yeatReservations = yeatReservations.filter(
    (r) => !isPastYeatTimeForToday(formattedDate, r.time)
  );

  // Convert to time format and remove duplicates
  const times = yeatReservations.map((reservation) => ({
    value: reservation.time,
    label: `${reservation.time} Laatste Kans`,
    isYeat: true,
    lastMinuteId: reservation.lastMinuteId,
  }));

  // Remove duplicates based on time value, keeping the first occurrence
  const uniqueTimes = times.filter(
    (time, index, arr) => arr.findIndex((t) => t.value === time.value) === index
  );

  // Sort times chronologically
  return uniqueTimes.sort((a, b) => a.value.localeCompare(b.value));
};
